/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

/* eslint-disable no-console */
import "src/app/index.css";
import "src/app/instantsearch.css";
import "react-toastify/dist/ReactToastify.css";
import "src/components/UI/Toast/toasts.css";
import "src/components/Block/BlockTypes/CodeBlock/prism-vs.css";
import "@uiw/react-textarea-code-editor/dist.css";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";
import React, { Suspense, useEffect, useState } from "react";

import Cookies from "js-cookie";
import type { AppProps } from "next/app";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { isElectron } from "react-device-detect";
import { useSelector } from "react-redux";
import { ThemeProvider } from "styled-components";

import { SupportedTheme, UpdateElectronInfo, UpdateState, UpdateStatus } from "@vienna/common";

import { GlobalStyles } from "src/app/globalStyles";
import { darkTheme, lightTheme } from "src/app/themes";
import { useCurrentUser } from "src/app/useCurrentUser";
import RouteProtector from "src/components/RouteProtector/RouteProtector";
import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper";
import { NoSSR } from "src/components/UI/NoSSR/NoSSR";
import { Update } from "src/components/Updating/Updating";
import { META_ROUTES } from "src/lib/auth";
import usePageTracking from "src/lib/hooks/usePageTracking";
import { AppState } from "src/store";
import { wrapper } from "src/store/store";

const Toast = dynamic(() => import("src/components/UI/Toast/Toast"));

const ThemeComponent = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const themeSetting = useSelector((state: AppState) => state.globalUI.uiSettings.theme);
  const loggedOut = !useCurrentUser();

  let theme;
  if (META_ROUTES.includes(router.route) || loggedOut) {
    theme = SupportedTheme.light;
  } else {
    theme = SupportedTheme[themeSetting];
  }

  return (
    <ThemeProvider theme={theme === SupportedTheme.dark ? darkTheme : lightTheme}>
      <GlobalStyles />
      {children}
    </ThemeProvider>
  );
};

function NextApp({ Component, pageProps }: AppProps) {
  const router = useRouter();

  const [download, setDownload] = useState<UpdateElectronInfo | null>(null);
  const [updateState, setUpdateState] = useState<UpdateState>("update-not-available");
  const [isMounted, setIsMounted] = useState(false);
  const [tokenCookie] = useState(Cookies.get("token") || "");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isElectron && typeof window !== "undefined") {
      const token = localStorage.getItem("token");
      if (token) {
        window.electronAPI?.authAPI.setToken(token);
        window.electronAPI?.receive("autoUpdateState", (data: UpdateStatus) => {
          console.debug(`Received ${JSON.stringify(data)} from main process`);
          if (data.updateState !== updateState) {
            setUpdateState(data.updateState);
          }
          if (data.download) {
            setDownload(data.download);
          }
        });
      }
    }
  }, []);

  usePageTracking();

  if (updateState === "download-progress") {
    return <Update progress={download?.percent || 0} />;
  }

  /**
   * Splash level route constants for determining SSR behavior and root url redirect location
   */

  // Only SSR on splash and plate
  const shouldSSR =
    META_ROUTES.includes(router.route) ||
    // don't ssr root splash page if we have a token, going to redirect to /home/mine
    !(tokenCookie && router.route === "/");

  return (
    <ConditionalWrapper condition={!shouldSSR} wrapper={(children) => <NoSSR>{children}</NoSSR>}>
      <>
        <ThemeComponent>
          <RouteProtector>
            <Component {...pageProps} />
          </RouteProtector>
        </ThemeComponent>
        {isMounted && (
          <Suspense fallback={<div />}>
            <Toast></Toast>
          </Suspense>
        )}
      </>
    </ConditionalWrapper>
  );
}

export default wrapper.withRedux(NextApp);
